import { createPortal } from 'react-dom';
import { Button } from '../components/ui';
import { money, pct, num } from '../lib/format';
import type { AdvanceResponse } from '../lib/types';

export function ResultModal({ res, onClose }: { res: AdvanceResponse; onClose: () => void }) {
  const r = res.result;
  // Portalled to <body>: rendered inline it is a child of GamePlay's
  // `space-y-5`, whose `margin-top` shifts a position:fixed box down by 20px —
  // the scrim would leave an undimmed strip at the top of the viewport.
  return createPortal(
    <div className="fixed inset-0 z-50 grid place-items-center bg-[color:var(--scrim)] p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-6" data-tut="result-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mb-1 text-xs uppercase tracking-wide text-mut">Quarter resolved</div>
        <h2 className="mb-4 text-2xl font-extrabold">
          {res.finished ? '🏁 Final quarter complete' : `Q${r.round} results`}
        </h2>
        <div className="grid grid-cols-2 gap-3">
          <Metric label="Revenue" value={money(r.revenue ?? null)} />
          <Metric label="Profit" value={money(r.profit ?? null)} tone={(r.profit ?? 0) < 0 ? 'bad' : 'good'} />
          <Metric label="Market share" value={pct(r.share ?? null)} />
          <Metric label="Units sold" value={r.unitsSold != null ? `${num(r.unitsSold)}k` : '—'} />
          <Metric label="Composite" value={res.game.composite != null ? String(Math.round(res.game.composite)) : '—'} tone="acc" />
          <Metric label="Cash" value={money(res.game.cash)} />
        </div>

        {(r.rndCompleted?.length ?? 0) > 0 && (
          <div className="mt-4 text-sm">
            <div className="text-mut">R&amp;D completed:</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {r.rndCompleted!.map((t, i) => (
                <span key={i} className="chip bg-acc/15 text-acc">{t.icon} {t.name}</span>
              ))}
            </div>
          </div>
        )}
        {(r.events?.length ?? 0) > 0 && (
          <div className="mt-3 text-sm">
            <div className="text-mut">Market events:</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {r.events!.map((e, i) => (
                <span key={i} className="chip bg-warn/15 text-warn">{e.icon} {e.label}</span>
              ))}
            </div>
          </div>
        )}

        <Button className="mt-6 w-full" onClick={onClose}>
          {res.finished ? 'View final review' : 'Continue to next quarter'}
        </Button>
      </div>
    </div>,
    document.body,
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' | 'acc' }) {
  const color = tone === 'good' ? 'text-good' : tone === 'bad' ? 'text-bad' : tone === 'acc' ? 'text-acc' : 'text-txt';
  return (
    <div className="rounded-sm border border-card-b bg-bg2/40 px-3 py-2">
      <div className="text-[11px] uppercase text-mut">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
}
